import useFetchApi, { FetchingStatus } from 'hooks/useFetchApi/useFetchApi'
import { OrderChannel } from 'constant/OrderChannels'
import { GiftCardSaleTopupProcessContract, GiftCardSaleType } from 'models'
import { useCallback, useMemo, useState } from 'react'
import paymentService from 'Services/api/payment.service'
import { OrderFulfilmentType } from 'store/models/sellModels'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import { useAppSelector } from 'store/useStoreHook'
import {
    OnSuccessfulTopupCallback,
    TopUpGiftCardInfo,
    TopUpResult,
} from './types'
import paymentSettingService from 'Services/api/paymentSetting.service'
import { ProcessingSaleCallback } from '../payment/Models/ProcessingSaleCallback'
import salesService from 'Services/api/sales.service'
import { OrderDetailModel } from '../payment/Models/OrderDetailModels'
import { PaymentResult } from '../payment/Models/PaymentResult'
import { MOCK_GIFT_CARD_ORDER_ID } from '../GiftCardPopup/BuyGiftCard/useBuyGiftCard'
import Guid from 'utilities/Guid'

interface UseTopUpGiftCardParams {
    topUpGiftCard?: TopUpGiftCardInfo
    onSuccessfulTopup: OnSuccessfulTopupCallback
}

function useTopupGiftCard({
    topUpGiftCard,
    onSuccessfulTopup,
}: UseTopUpGiftCardParams) {
    const pinCode = useAppSelector((state) => state.otherModuleAction.pin)
    const [settings, getSettings, getSettingsStatus] = useFetchApi(
        paymentSettingService.getSystemPaymentById,
    )
    const [openPaymentDialog, setOpenPaymentDialog] = useState(false)
    const [topUpAmount, setTopUpAmount] = useState(0)
    const [currentTopUpResult, setCurrentTopUpResult] = useState<TopUpResult>()

    const loadDependencies = useCallback(() => {
        if (!settings) getSettings(PAYMENT_TYPE_ID.GIFTCARD)
    }, [settings, getSettings])

    const onChangeTopUpAmount = useCallback((newAmount: number) => {
        setTopUpAmount(newAmount)
    }, [])
    const [giftCardSaleID, setGiftCardSaleID] = useState('')
    const saleDetail: OrderDetailModel = useMemo(() => {
        return {
            orderID: giftCardSaleID || MOCK_GIFT_CARD_ORDER_ID,
            orderLines: [],
            orderNo: '',
            serviceChargeAmount: 0,
            totalTaxAmount: 0,
            totalDueAmount: topUpAmount,
            subtotal: 0,
            discountAmount: 0,
            taxExclusiveAmount: 0,
            taxInclusiveAmount: 0,
        }
    }, [giftCardSaleID, topUpAmount])

    const onProcessingTopUpPayment: ProcessingSaleCallback = useCallback(
        async (payment, customerID, currencyID) => {
            if (!topUpGiftCard) return {}

            const payload: GiftCardSaleTopupProcessContract = {
                paymentCurrencyID: currencyID!,
                giftCardID: topUpGiftCard.giftCardID,
                topupPrice: payment.tenderedAmount! - payment.changeAmount!,
                systemOrderChannelID: OrderChannel.QuickService,
                orderFulfillmentTypeID: OrderFulfilmentType.DINE_IN,
                type: GiftCardSaleType.Topup,
                customerID: customerID,
                payments: [payment],
                pinCode,
                giftCardSaleID: Guid.empty,
            }

            const result = (await paymentService.topUpGiftCard(payload)).data
            setGiftCardSaleID(result.giftCardSaleID!)

            return { ...result, saleID: result.giftCardSaleID! }
        },
        [pinCode, topUpGiftCard],
    )

    const onRequestPayment = useCallback((value: boolean) => {
        setOpenPaymentDialog(value)
    }, [])

    const onClosedReceiptDialog = useCallback(() => {
        onSuccessfulTopup(
            currentTopUpResult!.giftCardSaleID,
            currentTopUpResult!.topUpAmount,
        )
        setCurrentTopUpResult(undefined)
    }, [currentTopUpResult, onSuccessfulTopup])
    const onResetValue = useCallback(() => {
        setTopUpAmount(0)
    }, [])
    const [, getGiftCardSaleByID] = useFetchApi(salesService.getGiftCardSale)
    const handleGetSaleDetails = useCallback(
        async (id: string) => {
            const res = await getGiftCardSaleByID(id)
            return { ...res, saleID: res?.giftCardSaleID! }
        },
        [getGiftCardSaleByID],
    )
    const onFinishSale = useCallback(
        (result: PaymentResult) => {
            setCurrentTopUpResult({
                giftCardSaleID: result.saleID!,
                topUpAmount: result.totalDueAmount ?? 0,
                currentBalance:
                    topUpGiftCard?.currentBalance
                        ?.add(result.totalDueAmount ?? 0)
                        ?.toNumber() ?? 0,
                changeAmount: result.changeAmount ?? 0,
                customerEmail: topUpGiftCard?.customerEmail,
            })
            onRequestPayment(false)
        },
        [
            onRequestPayment,
            topUpGiftCard?.currentBalance,
            topUpGiftCard?.customerEmail,
        ],
    )
    return {
        loading: getSettingsStatus === FetchingStatus.LOADING,
        loadDependencies,
        giftCardPaymentSettings: settings,
        topUpAmount,
        onChangeTopUpAmount,
        onProcessingTopUpPayment,
        onRequestPayment,
        openPaymentDialog,
        currentTopUpResult,
        onClosedReceiptDialog,
        onResetValue,
        handleGetSaleDetails,
        saleDetail,
        setCurrentTopUpResult,
        onFinishSale,
    }
}

export default useTopupGiftCard
